/*eslint  @typescript-eslint/no-use-before-define: 0*/
import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { OrderGetServiceTypeAttributeItem } from "@/models/api/orders/temporary/OrderGetServiceTypeAttributes";
import { OrderItemPart } from "@/models/api/orders/temporary/OrderGetTemporaryOrderItems";
import {
  OrderItemChangeAttributeParams,
  OrderItemChangeCommandParams,
} from "@/models/api/commands/order/OrderItemChangeCommand";
import { SnackbarModel } from "@/models/components/popup/Snackbar";
import i18n from "@/plugins/i18n";
import { filterCapitalize } from "@/filters/capitalize";
import { ServiceTypeListItem } from "@/models/api/serviceTypes/ServiceTypeListItem";
import { OrderGetTemporaryCalculateValueRequest } from "@/models/api/orders/temporary/OrderGetTemporaryCalculateValue";
import { CALCULATE_ORDER_ITEM_VALUE } from "@/static/calculateOrderItemValueTypes";
import { getAttributesForOrderItem, getCalculatedValue, postValidateAttributes } from "@/requests/order/orderAddEdit";
import { AttributeItem } from "@/models/api/bookings/BookingGetTemporaryItem";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { ErrorHiddenFromConsole } from "@/models/ErrorHiddenFromConsole";
import dayjs from "dayjs";
import { DEFAULT_DATE_FORMAT } from "@/static/dateFormats";
import { AlertModelInterface } from "@/models/components/popup/Alert";
import { MESSAGE_TYPES } from "@/static/messageTypes";
import { DOCUMENT_TYPES } from "@/static/documentTypes";

export enum ORDER_ITEM_EDIT_STEP {
  SERVICE_TYPE = 1,
  FINANCES = 2,
  ATTRIBUTES = 3,
}

export interface OrderAddEditOrderItemStateInterface {
  attributes: Array<AttributeItem> | null;
  isEditDialog: boolean;
  isLoading: boolean;
  editStep: number;
  orderItemId: string | null;
  commandParams: null | OrderItemChangeCommandParams;
  availableOrderItemEditSteps: Array<ORDER_ITEM_EDIT_STEP>;
  isValidateAttributes: boolean;
  skipAttributeValidation: boolean;
}

export const OrderAddEditOrderItemState: OrderAddEditOrderItemStateInterface = {
  attributes: null,
  isEditDialog: false,
  isLoading: false,
  editStep: 0,
  orderItemId: null,
  commandParams: null,
  availableOrderItemEditSteps: [],
  isValidateAttributes: false,
  skipAttributeValidation: false,
};

const OrderAddEditOrderItemModule = defineModule({
  state: OrderAddEditOrderItemState as OrderAddEditOrderItemStateInterface,
  namespaced: true as const,
  mutations: {
    setAttributes(state, param: Array<AttributeItem> | null) {
      state.attributes = param;
    },
    setIsDialog(state, param: boolean) {
      state.isEditDialog = param;
    },
    setEditStep(state, param: ORDER_ITEM_EDIT_STEP) {
      state.editStep = param;
    },
    setIsLoading(state, param: boolean) {
      state.isLoading = param;
    },
    setOrderItem(state, param: string | null) {
      state.orderItemId = param;
    },
    setCommandParams(state, param: OrderItemChangeCommandParams) {
      state.commandParams = param;
    },
    setAvailableOrderItemEditSteps(state, params: { orderItem: OrderItemPart | null; isFormalCorrection: boolean }) {
      const steps = [];
      if (params.orderItem === null) {
        //Nowa usługa
        steps.push(ORDER_ITEM_EDIT_STEP.SERVICE_TYPE);
        steps.push(ORDER_ITEM_EDIT_STEP.FINANCES);
        steps.push(ORDER_ITEM_EDIT_STEP.ATTRIBUTES);
      } else {
        if (
          params.orderItem.tradeTerm === null &&
          params.orderItem.orderItems.length === 0 &&
          !params.isFormalCorrection
        ) {
          steps.push(ORDER_ITEM_EDIT_STEP.FINANCES);
        }
        if (params.orderItem.parentOrderItemId === null) {
          steps.push(ORDER_ITEM_EDIT_STEP.ATTRIBUTES);
        }
      }
      state.availableOrderItemEditSteps = steps;
      state.editStep = steps[0];
    },
    setIsValidateAttributes(state, param: boolean) {
      state.isValidateAttributes = param;
    },
    setSkipAttributeValidation(state, param: boolean) {
      state.skipAttributeValidation = param;
    },
  },
  actions: {
    async setAvailableOrderItemEditSteps(context, orderItem: OrderItemPart | null) {
      const { rootState, commit, state, dispatch } = _moduleActionContext(context);
      commit.setAttributes(null);
      commit.setAvailableOrderItemEditSteps({
        orderItem,
        isFormalCorrection: rootState.orderAddEdit.orderSection?.isFormalCorrection ?? false,
      });
      if (
        state.editStep !== ORDER_ITEM_EDIT_STEP.SERVICE_TYPE &&
        state.availableOrderItemEditSteps.indexOf(ORDER_ITEM_EDIT_STEP.ATTRIBUTES) !== -1
      )
        dispatch.downloadAttributes();
    },

    async downloadAttributes(context) {
      const { commit, rootState, state } = _moduleActionContext(context);
      if (rootState.orderAddEdit.operationId !== null && state.commandParams !== null) {
        const resp = await getAttributesForOrderItem(rootState.orderAddEdit.operationId, {
          OrderItemId: state.commandParams.Id,
          ServiceTypeId: state.commandParams.ServiceTypeId ?? -1,
        });
        commit.setAttributes(resp);
        commit.setIsValidateAttributes(true);
      }
    },
    async addOrderItem(context) {
      const { dispatch, commit, rootState, rootDispatch } = _moduleActionContext(context);
      const orderCurrency = rootState.orderAddEdit.orderSection?.currencyCode ?? "PLN";
      const exchange = await rootDispatch.exchangeRate.getLastExchangeRate({
        exchangeDate: dayjs(rootState.orderAddEdit.orderSection?.saleDate).add(-1, "day").format(DEFAULT_DATE_FORMAT),
        currency: orderCurrency,
        skipAlert: true,
      });
      const orderItemChangeCommandParams = new OrderItemChangeCommandParams();
      orderItemChangeCommandParams.Currency = orderCurrency;
      orderItemChangeCommandParams.PurchaseNetPriceCurrency = orderCurrency;
      orderItemChangeCommandParams.ExchangeRate = exchange.exchangeRateValue;
      orderItemChangeCommandParams.ExchangeDate = exchange.exchangeDate;
      orderItemChangeCommandParams.PurchaseNetPriceExchangeDate = exchange.exchangeDate;
      orderItemChangeCommandParams.PurchaseNetPriceExchangeRate = exchange.exchangeRateValue;

      commit.setCommandParams(orderItemChangeCommandParams);
      dispatch.setAvailableOrderItemEditSteps(null);
      commit.setIsDialog(true);
    },
    async editOrderItem(context, param: OrderItemPart) {
      const { dispatch, commit } = _moduleActionContext(context);

      commit.setCommandParams({
        Currency: param.currency,
        ExchangeDate: param.exchangeDate,
        ExchangeRate: param.exchangeRate,
        Gross: param.gross,
        GrossInCurrency: param.grossInCurrency,
        Id: param.id,
        ImportedFromDocumentId: param.importedFromDocumentId,
        Modifier: param.modifier,
        Name: param.name,
        NetPrice: param.netPrice,
        NetPriceInCurrency: param.netPriceInCurrency,
        NetValue: param.netValue,
        NetValueInCurrency: param.netValueInCurrency,
        OrderItemAttributes: [],
        OwnerId: param.ownerId,
        PurchaseNetPrice: param.purchaseNetPrice,
        PurchaseNetPriceCurrency: param.purchaseNetPriceCurrency,
        PurchaseNetPriceExchangeDate: param.purchaseNetPriceExchangeDate,
        PurchaseNetPriceExchangeRate: param.purchaseNetPriceExchangeRate,
        PurchaseNetPriceInCurrency: param.purchaseNetPriceInCurrency,
        Quantity: param.quantity,
        ServiceTypeId: param.serviceTypeId,
        ServiceTypeCode: param.serviceTypeCode,
        Unit: param.unit,
        VAT: param.vat,
        VATAmount: param.vatAmount,
        VATAmountInCurrency: param.vatAmountInCurrency,
      } as OrderItemChangeCommandParams);

      dispatch.setAvailableOrderItemEditSteps(param);
      commit.setOrderItem(param.id);
      commit.setIsDialog(true);
    },
    async copyAttributeToOtherOrderItems(context, param: OrderGetServiceTypeAttributeItem) {
      const { state, rootDispatch } = _moduleActionContext(context);
      rootDispatch.orderAddEdit.commands.copyAttributeToOtherOrderItems({
        attribute: param,
        orderItemId: state.orderItemId as string,
      });
    },
    async validateAttributes(context): Promise<boolean> {
      const { state, rootState } = _moduleActionContext(context);
      if (state.attributes != null && rootState.orderAddEdit.operationId != null)
        return await postValidateAttributes(
          rootState.orderAddEdit.operationId,
          state.attributes,
          state.skipAttributeValidation
        );
      return false;
    },
    async selectServiceType(context, param: ServiceTypeListItem): Promise<void> {
      const { dispatch, state, commit, rootState, rootDispatch } = _moduleActionContext(context);

      if (param.documentTypeId == DOCUMENT_TYPES.InternalDocument) {
        await rootDispatch.popup.addAlert({
          content: filterCapitalize(i18n.t("serviceTypeDocumentTypeIsNotAllowedOnOrder")),
          title: filterCapitalize(i18n.t("warning")),
          type: MESSAGE_TYPES.Warning,
        } as AlertModelInterface);
        return;
      }

      const commandParams = state.commandParams ?? new OrderItemChangeCommandParams();
      commandParams.VAT = param.vat;
      commandParams.ServiceTypeId = param.id;
      commandParams.Name = param.name;
      commandParams.ServiceTypeCode = param.code;
      commandParams.Unit = param.unit ?? "";
      const orderCurrency = rootState.orderAddEdit.orderSection?.currencyCode ?? "PLN";
      const exchange = await rootDispatch.exchangeRate.getLastExchangeRate({
        exchangeDate: dayjs(rootState.orderAddEdit.orderSection?.saleDate).add(-1, "day").format(DEFAULT_DATE_FORMAT),
        currency: orderCurrency,
        skipAlert: true,
      });
      commandParams.Currency = orderCurrency;
      commandParams.PurchaseNetPriceCurrency = orderCurrency;
      commandParams.ExchangeRate = exchange.exchangeRateValue;
      commandParams.ExchangeDate = exchange.exchangeDate;
      commandParams.PurchaseNetPriceExchangeDate = exchange.exchangeDate;
      commandParams.PurchaseNetPriceExchangeRate = exchange.exchangeRateValue;
      commandParams.PurchaseNetPrice = 0;
      commandParams.PurchaseNetPriceInCurrency = 0;
      commandParams.Quantity = 1;
      commandParams.NetPrice = 0;
      commandParams.NetPriceInCurrency = 0;
      commandParams.NetValue = 0;
      commandParams.NetValueInCurrency = 0;
      commandParams.Gross = 0;
      commandParams.GrossInCurrency = 0;
      commandParams.VATAmount = 0;
      commandParams.VATAmountInCurrency = 0;
      commit.setCommandParams(commandParams);
      dispatch.applyChanges();
    },
    async preventFromAccidentallySettingZeroAmount(context) {
      const { state, rootDispatch } = _moduleActionContext(context);
      if (state.commandParams !== null && state.commandParams.PurchaseNetPrice === 0) {
        const confirm = await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(i18n.t("doYouReallyWantToConfirmZeroBuyoutAmount?")),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("back")),
                color: "warning",
                value: false,
                outlined: true,
              },
              {
                text: filterCapitalize(i18n.t("confirm")),
                color: "warning",
                value: true,
              },
            ]
          )
        );
        if (confirm === false) {
          throw new ErrorHiddenFromConsole("User cancelled action");
        }
      }
    },
    async applyChanges(context): Promise<void> {
      const { dispatch, state, rootDispatch, commit } = _moduleActionContext(context);
      const param = state.commandParams;
      if (param) {
        switch (state.editStep) {
          case ORDER_ITEM_EDIT_STEP.SERVICE_TYPE:
            if (!param.ServiceTypeId)
              rootDispatch.popup.addSnackbar(
                new SnackbarModel(filterCapitalize(i18n.t("serviceTypeSelectionIsRequired").toString()), "error")
              );
            else {
              await dispatch.stepUp();
              dispatch.downloadAttributes();
            }
            break;
          case ORDER_ITEM_EDIT_STEP.FINANCES:
            await dispatch.preventFromAccidentallySettingZeroAmount();
            if (
              state.availableOrderItemEditSteps.indexOf(state.editStep) <
              state.availableOrderItemEditSteps.length - 1
            )
              await dispatch.stepUp();
            else {
              await rootDispatch.orderAddEdit.commands.orderItemChange(param);
              commit.setIsDialog(false);
            }
            break;
          case ORDER_ITEM_EDIT_STEP.ATTRIBUTES:
            if (state.attributes) {
              commit.setIsValidateAttributes(true);
              const validate = await dispatch.validateAttributes();
              if (validate) {
                param.OrderItemAttributes = state.attributes.map(
                  (v) =>
                    ({
                      AttributePrototypeId: v.attributePrototypeId,
                      DataType: v.dataType,
                      IsPrintable: v.isPrintable,
                      Label: v.label,
                      Ordinal: v.ordinal,
                      Value: v.value,
                    } as OrderItemChangeAttributeParams)
                );
                await rootDispatch.orderAddEdit.commands.orderItemChange(param);
                commit.setSkipAttributeValidation(false);
                commit.setIsDialog(false);
              }
            }
            break;
          default:
            break;
        }
      }
    },
    async stepUp(context) {
      const { state, commit } = _moduleActionContext(context);
      const indexOfAvailableStep = state.availableOrderItemEditSteps.indexOf(state.editStep);
      commit.setEditStep(state.availableOrderItemEditSteps[indexOfAvailableStep + 1]);
    },
    async stepBack(context): Promise<void> {
      const { state, commit } = _moduleActionContext(context);
      const indexOfAvailableStep = state.availableOrderItemEditSteps.indexOf(state.editStep);
      commit.setEditStep(state.availableOrderItemEditSteps[indexOfAvailableStep - 1]);
    },
    async calculateOrderItemValue(
      context,
      param: {
        value: number | string;
        calculateType: CALCULATE_ORDER_ITEM_VALUE;
      }
    ) {
      const { state, commit } = _moduleActionContext(context);
      if (state.commandParams !== null) {
        const calculatedValues = await getCalculatedValue(
          new OrderGetTemporaryCalculateValueRequest(
            typeof state.commandParams.Quantity !== "number"
              ? parseFloat(state.commandParams.Quantity)
              : state.commandParams.Quantity,
            typeof param.value !== "number" ? parseFloat(param.value) : param.value,
            state.commandParams.VAT,
            typeof state.commandParams.ExchangeRate === "string"
              ? parseFloat(state.commandParams.ExchangeRate)
              : state.commandParams.ExchangeRate,
            param.calculateType
          )
        );
        const params = state.commandParams;
        params.Quantity = calculatedValues.quantity;
        params.NetPrice = calculatedValues.netPrice;
        params.NetValue = calculatedValues.netValue;
        params.NetPriceInCurrency = calculatedValues.netPriceInCurrency;
        params.NetValueInCurrency = calculatedValues.netValueInCurrency;
        params.VATAmount = calculatedValues.vatAmount;
        params.VATAmountInCurrency = calculatedValues.vatAmountInCurrency;
        params.Gross = calculatedValues.gross;
        params.GrossInCurrency = calculatedValues.grossInCurrency;
        params.Modifier = calculatedValues.modifier;
        commit.setCommandParams(params);
      }
    },
  },
});

export default OrderAddEditOrderItemModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, OrderAddEditOrderItemModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, OrderAddEditOrderItemModule);
